import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { ErrorReportService } from '../services/error-report.service';

function extractMessage(error: any): string {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  try { return JSON.stringify(error); } catch { return String(error); }
}

export function isChunkLoadError(error: any): boolean {
  if (!error) return false;
  const message = extractMessage(error).toLowerCase();
  // "Loading chunk" (Angular/webpack's own ChunkLoadError) and "Failed to fetch dynamically
  // imported module" (native ESM dynamic import, what actually surfaces on the app-routing
  // module's loadComponent()/loadChildren() failures — this string is why the Router's own
  // NavigationError path needed its own check, not just GlobalErrorHandler's).
  return message.includes('chunkloaderror')
    || message.includes('loading chunk')
    || message.includes('failed to fetch dynamically imported module')
    || message.includes('error loading dynamically imported module');
}

// A lazy-loaded chunk 404s when a browser tab open across a deployment still holds the
// previous build's main.js, which points to chunk hashes the server no longer serves. One
// reload picks up the current main.js/chunks; the sessionStorage guard stops a reload loop
// if the error persists for another reason (e.g. the server is actually down). Shared by
// GlobalErrorHandler (catches most thrown ChunkLoadErrors) and app.component.ts's
// NavigationError subscription (the Angular Router can swallow a lazy route's load failure
// internally and only ever emit it as a NavigationError, never as an uncaught exception —
// in that case GlobalErrorHandler.handleError() is never invoked at all).
export function reloadOnChunkLoadErrorOnce(error: any): boolean {
  if (!isChunkLoadError(error)) return false;
  const key = 'tms-chunk-reload-attempted';
  if (sessionStorage.getItem(key)) return true; // already tried this session — don't loop
  sessionStorage.setItem(key, Date.now().toString());
  window.location.reload();
  return true;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  // Lazy injection via Injector to avoid circular dependency:
  // ErrorHandler → HttpClient → Interceptor → ErrorHandler
  private errorReportService?: ErrorReportService;

  constructor(private injector: Injector) {}

  handleError(error: any): void {
    // Always preserve native console output
    console.error(error);

    try {
      if (reloadOnChunkLoadErrorOnce(error)) return;

      if (this.shouldSkip(error)) return;

      if (!this.errorReportService) {
        this.errorReportService = this.injector.get(ErrorReportService);
      }

      const message = extractMessage(error);
      const stack = error?.stack ?? error?.ngDebugContext ?? undefined;

      this.errorReportService.report('RUNTIME', message, { stackTrace: stack });
    } catch {
      // Guard: never let error reporting itself throw
    }
  }

  private shouldSkip(error: any): boolean {
    if (!error) return true;
    // Already-handled HTTP errors (re-thrown by interceptor)
    if (error?.status !== undefined) return true;
    return false;
  }
}
